import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Crown, 
  Check, 
  Zap,
  Shield,
  TrendingUp,
  Headphones,
  Sparkles,
  Star
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const Premium = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("premium_yearly");

  useEffect(() => {
    fetchPremiumStatus();
    
    // Check for subscription callback
    const subscriptionStatus = searchParams.get("subscription");
    const sessionId = searchParams.get("session_id");
    
    if (subscriptionStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (subscriptionStatus === "cancelled") {
      toast.error("Subscription cancelled");
    }
  }, [searchParams]);

  const fetchPremiumStatus = async () => {
    try {
      const token = localStorage.getItem("stelx_token");
      const response = await axios.get(`${API}/premium/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumStatus(response.data);
    } catch (error) {
      console.error("Error fetching premium status:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please check your email.");
      return;
    }

    try {
      const token = localStorage.getItem("stelx_token");
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid") {
        toast.success("Welcome to Stelx Premium! 🎉");
        fetchPremiumStatus();
        // Clear URL params
        navigate("/premium", { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem("stelx_token");
      const response = await axios.post(
        `${API}/payments/stripe/subscription`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast.error("Failed to start subscription. Please try again.");
      setSubscribing(false);
    }
  };

  const benefits = [
    { icon: Zap, title: "Zero Transfer Fees", desc: "Send money without any fees" },
    { icon: TrendingUp, title: "Lower FX Rates", desc: "1% vs 2% on currency exchange" },
    { icon: Shield, title: "Higher Limits", desc: "J$500,000 daily transfer limit" },
    { icon: Headphones, title: "Priority Support", desc: "24/7 dedicated support line" },
    { icon: Sparkles, title: "Premium Badge", desc: "Stand out with a verified badge" },
    { icon: Star, title: "Advanced Analytics", desc: "Detailed spending insights" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isPremium = premiumStatus?.is_premium;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-5 py-4 flex items-center gap-4 max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-white/5 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-semibold text-xl">Stelx Premium</h1>
          {isPremium && (
            <span className="ml-auto px-2 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-medium text-white">
              Active
            </span>
          )}
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-6">
        {/* Hero */}
        <div className="relative rounded-2xl overflow-hidden" style={{
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 50%, rgba(15, 23, 42, 0.95) 100%)'
        }}>
          <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
          <div className="relative z-10 p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-white mb-2">
              {isPremium ? "You're Premium!" : "Upgrade to Premium"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isPremium 
                ? `Your subscription is active until ${new Date(premiumStatus.premium_until).toLocaleDateString()}`
                : "Unlock exclusive features and save on every transaction"
              }
            </p>
          </div>
        </div>

        {/* Current Status for Premium Users */}
        {isPremium && (
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading font-semibold mb-4">Your Benefits</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">0%</p>
                <p className="text-xs text-muted-foreground">Transfer Fee</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-emerald-400">1%</p>
                <p className="text-xs text-muted-foreground">FX Fee</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">J$500K</p>
                <p className="text-xs text-muted-foreground">Daily Limit</p>
              </div>
              <div className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">24/7</p>
                <p className="text-xs text-muted-foreground">Priority Support</p>
              </div>
            </div>
          </div>
        )}

        {/* Benefits List */}
        {!isPremium && (
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading font-semibold mb-4">Premium Benefits</h3>
            <div className="space-y-4">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{benefit.title}</p>
                      <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pricing Plans */}
        {!isPremium && (
          <div className="space-y-3">
            <h3 className="font-heading font-semibold">Choose Your Plan</h3>
            
            {/* Yearly Plan */}
            <button
              onClick={() => setSelectedPlan("premium_yearly")}
              className={`w-full glass rounded-xl p-4 text-left transition-all ${
                selectedPlan === "premium_yearly" 
                  ? "ring-2 ring-amber-500 bg-amber-500/10" 
                  : "hover:bg-white/5"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-white">Annual</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                      Save 17%
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-white">$99.99<span className="text-sm font-normal text-muted-foreground">/year</span></p>
                  <p className="text-xs text-muted-foreground mt-1">~$8.33/month • 2 months free</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === "premium_yearly" 
                    ? "border-amber-500 bg-amber-500" 
                    : "border-white/20"
                }`}>
                  {selectedPlan === "premium_yearly" && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
            </button>

            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan("premium_monthly")}
              className={`w-full glass rounded-xl p-4 text-left transition-all ${
                selectedPlan === "premium_monthly" 
                  ? "ring-2 ring-amber-500 bg-amber-500/10" 
                  : "hover:bg-white/5"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <span className="font-semibold text-white">Monthly</span>
                  <p className="text-2xl font-bold text-white">$9.99<span className="text-sm font-normal text-muted-foreground">/month</span></p>
                  <p className="text-xs text-muted-foreground mt-1">Cancel anytime</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === "premium_monthly" 
                    ? "border-amber-500 bg-amber-500" 
                    : "border-white/20"
                }`}>
                  {selectedPlan === "premium_monthly" && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
            </button>

            {/* Subscribe Button */}
            <Button
              onClick={() => handleSubscribe(selectedPlan)}
              disabled={subscribing}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
            >
              {subscribing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Crown className="w-4 h-4 mr-2" />
                  Subscribe Now
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Secure payment via Stripe. Cancel anytime.
            </p>
          </div>
        )}

        {/* Compare Plans */}
        {!isPremium && (
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading font-semibold mb-4">Free vs Premium</h3>
            <div className="space-y-3">
              {[
                { feature: "Transfer Fee", free: "1.5%", premium: "0%" },
                { feature: "FX Fee", free: "2%", premium: "1%" },
                { feature: "Daily Limit", free: "J$100K", premium: "J$500K" },
                { feature: "Priority Support", free: "❌", premium: "✓" },
                { feature: "Premium Badge", free: "❌", premium: "✓" },
                { feature: "Advanced Analytics", free: "❌", premium: "✓" }
              ].map((row, index) => (
                <div key={index} className="grid grid-cols-3 py-2 border-b border-white/5 last:border-0">
                  <span className="text-sm text-muted-foreground">{row.feature}</span>
                  <span className="text-sm text-center text-white/50">{row.free}</span>
                  <span className="text-sm text-center text-amber-400 font-medium">{row.premium}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Premium;
